import React, { useContext } from 'react';
import { LocaleContext } from '../../../components/LocaleContext';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { postRequest } from '../../../api/ApiController';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { SEMAFOR_COLOR_OPTIONS, getSemaforColorClass } from '../../../utils/moduleSettings';
import { SelectButton } from 'primereact/selectbutton';
import { Dropdown } from 'primereact/dropdown';
import { uiT } from '../../../utils/uiTranslate';

// Konvencia: Zapnuté (1) = áno, zobrazovať (vyradené/deaktivované). Vypnuté (0) = nie, skryť.
const getOptions = () => [
  { label: uiT('Common.Off'), value: '0' },
  { label: uiT('Common.On'), value: '1' },
];

const COLOR_VALUES = ['red', 'orange', 'green', 'gray', 'blue', 'black'];

// Farby pre zoznamy – len buckety, ktoré sa skutočne zobrazujú v CalibrationStateList, GaugeCalibrationList, CalibrationCompanyCertStateList (0-, 15-, 30-, 45-, 60-, 61+, DEA)
const getSemaforKeys = () => [
  { key: 'cal_semafor_0', label: uiT('Common.Expiry.AfterRange') },
  { key: 'cal_semafor_15', label: uiT('Common.Expiry.Days15Range') },
  { key: 'cal_semafor_30', label: uiT('Common.Expiry.Days30Range') },
  { key: 'cal_semafor_45', label: uiT('Common.Expiry.Days45Range') },
  { key: 'cal_semafor_60', label: uiT('Common.Expiry.Days60Range') },
  { key: 'cal_semafor_60_plus', label: uiT('Common.Expiry.Days61Plus') },
  { key: 'cal_semafor_dea', label: uiT('Calibration.DEA.Decommissioned') },
];

// Farby pre grafy v Náhľade kalibrácií (PreviewCal) – 4 segmenty koláča (0-, 15-, 30-, OK) + 2 segmenty pre „načas / po termíne“
const getGraphCalibrationKeys = () => [
  { key: 'cal_graph_0', label: uiT('Common.Expiry.After') },
  { key: 'cal_graph_15', label: uiT('Common.Expiry.Days15Short') },
  { key: 'cal_graph_30', label: uiT('Common.Expiry.Days30Short') },
  { key: 'cal_graph_ok', label: uiT('Calibration.FitOK') },
  { key: 'cal_graph_on_time', label: uiT('Common.OnTime') },
  { key: 'cal_graph_late', label: uiT('Common.Overdue') },
];

function getSetting(module_settings, key, defaultValue = '0') {
  const row = (module_settings || []).find(s => s.module === 'calibration' && s.key === key);
  const v = row ? row.value : defaultValue;
  if (v == null || v === '') return defaultValue;
  // Vždy vráť '0' alebo '1' pre boolean, aby SelectButton správne zhodoval (0 !== '0')
  return (v === 0 || v === '0') ? '0' : (v === 1 || v === '1') ? '1' : String(v);
}

export default function CalibrationGeneralSettings() {
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast } = useContext(UtilContext);
  const { t } = useContext(LocaleContext);

  const module_settings = state.module_settings || [];

  const gaugesExcludedByGauge = getSetting(module_settings, 'show_gauges_excluded_list_by_gauge', '0');
  const gaugesExcludedByState = getSetting(module_settings, 'show_gauges_excluded_list_by_state', '0');
  const deactivatedCertsByCompany = getSetting(module_settings, 'show_deactivated_certs_list_by_company');
  const deactivatedCertsByState = getSetting(module_settings, 'show_deactivated_certs_list_by_state');
  const excludedCompaniesByCompany = getSetting(module_settings, 'show_excluded_calibration_companies_list_by_company');
  const excludedCompaniesByState = getSetting(module_settings, 'show_excluded_calibration_companies_list_by_state');

  const settingGaugesByGauge = module_settings.find(s => s.module === 'calibration' && s.key === 'show_gauges_excluded_list_by_gauge');
  const settingGaugesByState = module_settings.find(s => s.module === 'calibration' && s.key === 'show_gauges_excluded_list_by_state');
  const settingDeactivatedCertsByCompany = module_settings.find(s => s.module === 'calibration' && s.key === 'show_deactivated_certs_list_by_company');
  const settingDeactivatedCertsByState = module_settings.find(s => s.module === 'calibration' && s.key === 'show_deactivated_certs_list_by_state');
  const settingExcludedCompaniesByCompany = module_settings.find(s => s.module === 'calibration' && s.key === 'show_excluded_calibration_companies_list_by_company');
  const settingExcludedCompaniesByState = module_settings.find(s => s.module === 'calibration' && s.key === 'show_excluded_calibration_companies_list_by_state');

  async function updateSetting(setting, value, currentValue) {
    const normalized = value === 0 || value === '0' ? '0' : (value === 1 || value === '1' ? '1' : null);
    if (normalized === null) return;
    if (normalized === (currentValue === 0 || currentValue === '0' ? '0' : '1')) return;
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFoundInDb') });
      return;
    }
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value: normalized }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value: normalized });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ValueChangedImmediate') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
    }
  }

  async function updateSemaforSetting(setting, value, currentValue) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
      return;
    }
    if (!COLOR_VALUES.includes(value)) return;
    if (value === currentValue) return;
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.SemaforColorChanged') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
    }
  }

  async function updateGraphColorSetting(setting, value, currentValue) {
    if (!setting) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 5000, detail: t('ServiceGeneralSettings.SettingNotFound') });
      return;
    }
    if (!COLOR_VALUES.includes(value)) return;
    if (value === currentValue) return;
    try {
      await postRequest(`/api/module_settings/update/${setting.id}`, { value }, true);
      dispatch({ type: 'update_module_setting', id: setting.id, value });
      toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('ServiceGeneralSettings.ColorChangedSuccess') });
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('Common.Error'), life: 3000, detail: e?.message || t('ServiceGeneralSettings.SaveFailed') });
    }
  }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">{t('CustomerGeneralSettings.Title')}</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-stretch">
          {/* Karta 1: Všeobecné nastavenia */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Zobrazenie_vyradenych_meradiel_v_zoznamoch_kalibracii')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.V_zozname_meradiel_sa_vyradene_meradla_vzdy_zobrazuju_Toto_n')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_meradla')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={gaugesExcludedByGauge}
                    onChange={(e) => updateSetting(settingGaugesByGauge, e.value, gaugesExcludedByGauge)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_stavu')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={gaugesExcludedByState}
                    onChange={(e) => updateSetting(settingGaugesByState, e.value, gaugesExcludedByState)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_deaktivovanych_certifikatov_v_zoznamoch')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Certifikaty_so_zadanym_datumom_deaktivacie_sa_standardne_v_z')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200 mb-6">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_firmy')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={deactivatedCertsByCompany}
                    onChange={(e) => updateSetting(settingDeactivatedCertsByCompany, e.value, deactivatedCertsByCompany)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_stavu')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={deactivatedCertsByState}
                    onChange={(e) => updateSetting(settingDeactivatedCertsByState, e.value, deactivatedCertsByState)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>

            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200 mt-2">{t('Ui.R67.Zobrazenie_vyradenych_kalibracnych_firiem')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Kalibracne_firmy_oznacene_ako_vyradene_v_karte_firmy_sa_stan')}</p>
            <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_firmy')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={excludedCompaniesByCompany}
                    onChange={(e) => updateSetting(settingExcludedCompaniesByCompany, e.value, excludedCompaniesByCompany)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
              <div className="flex flex-nowrap items-center gap-3">
                <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Zoznam_podla_stavu')}</label>
                <div className="shrink-0 min-w-[140px]">
                  <SelectButton
                    value={excludedCompaniesByState}
                    onChange={(e) => updateSetting(settingExcludedCompaniesByState, e.value, excludedCompaniesByState)}
                    options={getOptions()}
                    optionLabel="label"
                    optionValue="value"
                    allowEmpty={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Karta 2: Farby pre zoznamy */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">
              Farby pre zoznamy
            </h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Tieto_farby_sa_pouzivaju_v_zoznamoch_kalibracie_semafor_expi')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {getSemaforKeys().map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'calibration' && s.key === key);
                const defaultValue = key === 'cal_semafor_0' ? 'red' : (key === 'cal_semafor_dea' ? 'gray' : (key === 'cal_semafor_60_plus' ? 'green' : 'orange'));
                const currentValue = getSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateSemaforSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Karta 3: Farby pre grafy */}
          <div className="p-6 bg-white rounded-md shadow-md flex flex-col min-h-0">
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Farby_pre_grafy_v_nahlade_kalibracii')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Nastavenie_farieb_grafov_na_stranke_Nahlad_kalibracii')}</p>
            <div className="pl-4 flex flex-col gap-1 border-l-2 border-gray-200">
              {getGraphCalibrationKeys().map(({ key, label }) => {
                const setting = module_settings.find(s => s.module === 'calibration' && s.key === key);
                const defaultValue = key === 'cal_graph_0' ? 'red' : (key === 'cal_graph_ok' || key === 'cal_graph_on_time' ? 'green' : (key === 'cal_graph_late' ? 'red' : 'orange'));
                const currentValue = getSetting(module_settings, key, defaultValue);
                const colorClass = getSemaforColorClass(currentValue);
                return (
                  <div key={key} className="grid grid-cols-1 sm:grid-cols-[minmax(200px,1fr)_220px] gap-3 items-center py-1.5">
                    <label className="text-sm font-medium text-gray-800 truncate">{label}</label>
                    <div className="flex items-center gap-2 shrink-0 min-w-0">
                      <span className={`inline-block w-5 h-5 rounded-full shrink-0 ${colorClass}`} title={currentValue} aria-hidden />
                      <Dropdown
                        value={currentValue}
                        options={SEMAFOR_COLOR_OPTIONS}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => updateGraphColorSetting(setting, e.value, currentValue)}
                        className="w-[180px] border border-gray-400 rounded"
                        placeholder={uiT('Common.Color')}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
